import { useCallback } from 'react';

import { HighlightMatch } from 'flow-builder/hooks/useHighlight';
import { styles as nodeStyles } from 'flow-builder/renderers/node/nodeStyles';
import type { EditorContext } from 'flow-builder/types/context';
import type { Relation } from 'flow-builder/types/relation';

type UseFocusOptions = Pick<EditorContext, 'state' | 'view'>;
type UseFocusReturn = (node: Relation) => void;

export function useNodeFocus({ state, view }: UseFocusOptions): UseFocusReturn {
  const { setViewState, setHighlightState } = state;

  const onFocus = useCallback<UseFocusReturn>(
    (node) => {
      if (!view.isReady) return;

      const { id, pageX, pageY } = node;

      const nodeCenterX = (pageX + nodeStyles.base.width / 2) * -1;
      const nodeCenterY = (pageY + nodeStyles.base.height / 2) * -1;

      const viewCenterPointX = (nodeCenterX + view.viewport.width / 2) | 0;
      const viewCenterPointY = (nodeCenterY + view.viewport.height / 2) | 0;

      view.viewport.position.x = viewCenterPointX;
      view.viewport.position.y = viewCenterPointY;
      view.viewport.setZoom(1);

      setViewState({ scale: 1 });
      setHighlightState({
        path: [null, id],
        match: HighlightMatch.Connection,
        overrides: { [id]: { match: HighlightMatch.Both } },
      });
    },
    [view.isReady, view.viewport, setViewState, setHighlightState],
  );

  return onFocus;
}
